import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { db } from '../../../../../firebase-client';
import {
  addDaysToDateISO,
  formatUKDateISO,
  formatUKDateLabel,
  todayUKDateISO,
  ukDateISOToUTCEnd,
  ukDateISOToUTCStart
} from '../../../../../utils/ukDates';
const DEFAULT_WIDGETS = ['summary', 'requestsVsSlots', 'statusBreakdown', 'requestsByType', 'leastRequested'];
const STATUS_COLORS = {
  booked: '#2f855a',
  pending: '#3182ce',
  unfilled: '#dd6b20',
  cancelled: '#e53e3e'
};
const WIDGETS = [{
  id: 'summary',
  label: 'Summary KPIs',
  description: 'Total requests, fill rate, and quick counts.'
}, {
  id: 'requestsVsSlots',
  label: 'Requests vs Slots',
  description: 'Daily requests compared to active boats.'
}, {
  id: 'requestsByType',
  label: 'Requests by Category',
  description: 'Which boat types are most requested.'
}, {
  id: 'statusBreakdown',
  label: 'Status Breakdown',
  description: 'Booked vs pending vs unfilled vs cancelled.'
}, {
  id: 'requestsTrend',
  label: 'Requests Over Time',
  description: 'Daily request volume trend.'
}, {
  id: 'topBoats',
  label: 'Top Requested Boats',
  description: 'Most requested boats by preference/assignment.'
}, {
  id: 'leastRequested',
  label: 'Least Requested Boats',
  description: 'Low-demand boats for buy/scrap decisions.'
}];
function toUTCDate(isoDate, endOfDay = false) {
  if (!isoDate) return null;
  return endOfDay ? ukDateISOToUTCEnd(isoDate) : ukDateISOToUTCStart(isoDate);
}
function toDateKey(ts) {
  return formatUKDateISO(ts);
}
function formatDateLabel(dateKey) {
  return formatUKDateLabel(dateKey, {
    day: '2-digit',
    month: 'short'
  });
}
function normalizeStatus(status) {
  const raw = String(status || 'pending').toLowerCase();
  if (raw === 'allocated' || raw === 'confirmed') return 'booked';
  if (raw === 'unfilled') return 'unfilled';
  if (raw === 'cancelled') return 'cancelled';
  return 'pending';
}
function loadWidgetPrefs(teamId) {
  if (!teamId || typeof window === 'undefined') return DEFAULT_WIDGETS;
  try {
    const raw = window.localStorage.getItem(`boatDashboardWidgets:${teamId}`);
    if (!raw) return DEFAULT_WIDGETS;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length) return parsed;
  } catch (e) {}
  return DEFAULT_WIDGETS;
}
function saveWidgetPrefs(teamId, widgets) {
  if (!teamId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`boatDashboardWidgets:${teamId}`, JSON.stringify(widgets));
  } catch (e) {}
}
export default function BoatBookingDashboardModal({
  isOpen,
  onClose,
  teamId,
  boats = []
}) {
  const defaultStart = todayUKDateISO();
  const defaultEnd = addDaysToDateISO(defaultStart, 30);
  const [filterStartISO, setFilterStartISO] = useState(defaultStart);
  const [filterEndISO, setFilterEndISO] = useState(defaultEnd);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [selectedWidgets, setSelectedWidgets] = useState(() => loadWidgetPrefs(teamId));
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    setSelectedWidgets(loadWidgetPrefs(teamId));
  }, [teamId]);
  useEffect(() => {
    saveWidgetPrefs(teamId, selectedWidgets);
  }, [teamId, selectedWidgets]);
  useEffect(() => {
    if (!isOpen || !teamId) return;
    const start = toUTCDate(filterStartISO, false);
    const end = toUTCDate(filterEndISO, true);
    if (!start || !end || start > end) {
      setRequests([]);
      setError('Please choose a valid date range.');
      return;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const qReq = query(collection(db, `teams/${teamId}/boatRequests`), where('date', '>=', Timestamp.fromDate(start)), where('date', '<=', Timestamp.fromDate(end)), orderBy('date', 'asc'));
        const snap = await getDocs(qReq);
        const rows = snap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setRequests(rows);
      } catch (e) {
        setError('Failed to load boat request data.');
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, teamId, filterStartISO, filterEndISO]);
  const boatNameById = useMemo(() => {
    const map = new Map();
    boats.forEach(b => map.set(b.id, b.name || 'Unnamed'));
    return map;
  }, [boats]);
  const boatTypeById = useMemo(() => {
    const map = new Map();
    boats.forEach(b => map.set(b.id, b.type || 'Other'));
    return map;
  }, [boats]);
  const activeBoatCount = useMemo(() => boats.filter(b => (b?.status || 'active') !== 'maintenance').length, [boats]);
  const filteredRequests = useMemo(() => {
    return requests.filter(r => {
      if (!includeCancelled && normalizeStatus(r.status) === 'cancelled') return false;
      return true;
    });
  }, [requests, includeCancelled]);
  const dateKeys = useMemo(() => {
    if (!filterStartISO || !filterEndISO || filterStartISO > filterEndISO) return [];
    const out = [];
    let cursor = filterStartISO;
    while (cursor && cursor <= filterEndISO) {
      out.push(cursor);
      cursor = addDaysToDateISO(cursor, 1);
    }
    return out;
  }, [filterStartISO, filterEndISO]);
  const dailyCounts = useMemo(() => {
    const map = {};
    filteredRequests.forEach(r => {
      const key = toDateKey(r.date);
      if (!key) return;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [filteredRequests]);
  const requestsVsSlotsData = useMemo(() => dateKeys.map(key => ({
    dateKey: key,
    label: formatDateLabel(key),
    Requests: dailyCounts[key] || 0,
    Slots: activeBoatCount
  })), [dateKeys, dailyCounts, activeBoatCount]);
  const statusCounts = useMemo(() => {
    return filteredRequests.reduce((acc, r) => {
      const bucket = normalizeStatus(r.status);
      acc[bucket] = (acc[bucket] || 0) + 1;
      return acc;
    }, {
      booked: 0,
      pending: 0,
      unfilled: 0,
      cancelled: 0
    });
  }, [filteredRequests]);
  const statusData = useMemo(() => Object.entries(statusCounts).map(([name, value]) => ({
    name: name[0].toUpperCase() + name.slice(1),
    value,
    color: STATUS_COLORS[name]
  })), [statusCounts]);
  const topRequestedBoats = useMemo(() => {
    const counts = {};
    filteredRequests.forEach(r => {
      const target = r.selectedBoatId || (Array.isArray(r.preferences) && r.preferences.length ? r.preferences[0] : null) || 'unspecified';
      const label = target === 'unspecified' ? 'No preference' : boatNameById.get(target) || target;
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).map(([label, count]) => ({
      label,
      count
    })).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [filteredRequests, boatNameById]);
  const requestCountsByBoatId = useMemo(() => {
    const counts = {};
    boats.forEach(b => {
      counts[b.id] = 0;
    });
    filteredRequests.forEach(r => {
      const target = r.selectedBoatId || (Array.isArray(r.preferences) && r.preferences.length ? r.preferences[0] : null);
      if (!target) return;
      counts[target] = (counts[target] || 0) + 1;
    });
    return counts;
  }, [filteredRequests, boats]);
  const requestsByType = useMemo(() => {
    const counts = {};
    boats.forEach(b => {
      const type = b.type || 'Other';
      counts[type] = counts[type] || {
        type,
        requests: 0,
        fleet: 0
      };
      counts[type].fleet += 1;
    });
    Object.entries(requestCountsByBoatId).forEach(([boatId, count]) => {
      const type = boatTypeById.get(boatId) || 'Other';
      counts[type] = counts[type] || {
        type,
        requests: 0,
        fleet: 0
      };
      counts[type].requests += count;
    });
    return Object.values(counts).sort((a, b) => b.requests - a.requests);
  }, [boats, requestCountsByBoatId, boatTypeById]);
  const leastRequestedBoats = useMemo(() => {
    const rows = boats.map(b => ({
      id: b.id,
      label: b.name || 'Unnamed',
      type: b.type || 'Other',
      count: requestCountsByBoatId[b.id] || 0
    }));
    return rows.sort((a, b) => a.count - b.count).slice(0, 6);
  }, [boats, requestCountsByBoatId]);
  const totalRequests = filteredRequests.length;
  const fillRate = totalRequests ? Math.round(statusCounts.booked / totalRequests * 100) : 0;
  const toggleWidget = id => {
    setSelectedWidgets(prev => {
      if (prev.includes(id)) return prev.filter(w => w !== id);
      return [...prev, id];
    });
  };
  if (!isOpen) return null;
  return <div className="modal-backdrop booking-dashboard-modal" onClick={onClose}>
      <div className="modal-content modal-content--xl" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Boat Booking Dashboard</h2>
          <button onClick={onClose} aria-label="Close">×</button>
        </div>

        <section className="dashboard-controls">
          <div className="controls-row">
            <label>
              From{' '}
              <input type="date" value={filterStartISO} onChange={e => setFilterStartISO(e.target.value)} />
            </label>
            <label>
              To{' '}
              <input type="date" value={filterEndISO} onChange={e => setFilterEndISO(e.target.value)} />
            </label>
            <label className="dashboard-checkbox">
              <input type="checkbox" checked={includeCancelled} onChange={e => setIncludeCancelled(e.target.checked)} />
              Include cancelled
            </label>
            <button type="button" className="btn btn--secondary btn--xs" onClick={() => setSelectedWidgets(DEFAULT_WIDGETS)}>
              Reset widgets
            </button>
          </div>

          <div className="dashboard-widget-picker">
            {WIDGETS.map(widget => <label key={widget.id} className="dashboard-widget-option">
                <input type="checkbox" checked={selectedWidgets.includes(widget.id)} onChange={() => toggleWidget(widget.id)} />
                <div>
                  <div>{widget.label}</div>
                  <div className="muted">{widget.description}</div>
                </div>
              </label>)}
          </div>
        </section>

        {loading && <p className="muted">Loading booking data…</p>}
        {error && !loading && <div className="modal-error">{error}</div>}

        {!loading && !error && <div className="dashboard-grid">
            {selectedWidgets.includes('summary') && <section className="dashboard-widget">
                <h3 className="section-title">Summary</h3>
                <div className="dashboard-kpis">
                  <div>
                    <div className="kpi-label">Total requests</div>
                    <div className="kpi-value">{totalRequests}</div>
                  </div>
                  <div>
                    <div className="kpi-label">Booked</div>
                    <div className="kpi-value">{statusCounts.booked}</div>
                  </div>
                  <div>
                    <div className="kpi-label">Pending</div>
                    <div className="kpi-value">{statusCounts.pending}</div>
                  </div>
                  <div>
                    <div className="kpi-label">Unfilled</div>
                    <div className="kpi-value">{statusCounts.unfilled}</div>
                  </div>
                  <div>
                    <div className="kpi-label">Fill rate</div>
                    <div className="kpi-value">{fillRate}%</div>
                  </div>
                </div>
              </section>}

            {selectedWidgets.includes('requestsVsSlots') && <section className="dashboard-widget">
                <h3 className="section-title">Requests vs Slots</h3>
                <div className="muted" style={{
            marginBottom: 6
          }}>
                  Slots are approximated from active boats.
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={requestsVsSlotsData}>
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Requests" fill="#3182ce" />
                    <Bar dataKey="Slots" fill="#2f855a" />
                  </BarChart>
                </ResponsiveContainer>
              </section>}

            {selectedWidgets.includes('statusBreakdown') && <section className="dashboard-widget">
                <h3 className="section-title">Status Breakdown</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                      {statusData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </section>}

            {selectedWidgets.includes('requestsTrend') && <section className="dashboard-widget">
                <h3 className="section-title">Requests Over Time</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={requestsVsSlotsData}>
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="Requests" stroke="#3182ce" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </section>}

            {selectedWidgets.includes('topBoats') && <section className="dashboard-widget">
                <h3 className="section-title">Top Requested Boats</h3>
                {topRequestedBoats.length === 0 ? <p className="muted">No requests in this range.</p> : <table className="table">
                    <thead>
                      <tr>
                        <th>Boat</th>
                        <th>Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRequestedBoats.map(row => <tr key={row.label}>
                          <td>{row.label}</td>
                          <td>{row.count}</td>
                        </tr>)}
                    </tbody>
                  </table>}
              </section>}

            {selectedWidgets.includes('requestsByType') && <section className="dashboard-widget">
                <h3 className="section-title">Requests by Category</h3>
                {requestsByType.length === 0 ? <p className="muted">No boats or request data available.</p> : <>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={requestsByType}>
                        <XAxis dataKey="type" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="requests" name="Requests" fill="#3182ce" />
                        <Bar dataKey="fleet" name="Fleet size" fill="#94a3b8" />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="muted" style={{
              marginTop: 6
            }}>
                      Use this to spot high-demand categories vs fleet size.
                    </div>
                  </>}
              </section>}

            {selectedWidgets.includes('leastRequested') && <section className="dashboard-widget">
                <h3 className="section-title">Least Requested Boats</h3>
                {leastRequestedBoats.length === 0 ? <p className="muted">No boats available.</p> : <table className="table">
                    <thead>
                      <tr>
                        <th>Boat</th>
                        <th>Category</th>
                        <th>Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leastRequestedBoats.map(row => <tr key={row.id}>
                          <td>{row.label}</td>
                          <td>{row.type}</td>
                          <td>{row.count}</td>
                        </tr>)}
                    </tbody>
                  </table>}
              </section>}
          </div>}
      </div>
    </div>;
}
